import { SDK_FATAL_ERRORS, SDK_PERMANENT_ERRORS, SDK_ERRORS, type SdkErrorCode } from '@riz/contracts/sdk';

export interface RizErrorShape {
  name: 'RizError';
  code: SdkErrorCode | string;
  message: string;
  details: unknown;
  /** Retrying cannot change the outcome. */
  permanent: boolean;
  /** The session is over; every queued call fails with this. */
  fatal: boolean;
}

export type RizError = Error & RizErrorShape;

export function rizError(code: string, message?: string, details?: unknown): RizError {
  const error = new Error(message ?? code) as RizError;
  error.name = 'RizError';
  error.code = code;
  error.details = details ?? null;
  error.permanent = SDK_PERMANENT_ERRORS.has(code);
  error.fatal = SDK_FATAL_ERRORS.has(code);
  return error;
}

export function rejectWith(code: string, message?: string, details?: unknown): Promise<never> {
  return Promise.reject(rizError(code, message, details));
}

export function isRizError(value: unknown): value is RizError {
  return value instanceof Error && value.name === 'RizError' && typeof (value as RizError).code === 'string';
}

/**
 * Anything crossing the transport may carry a code this build has never heard
 * of. Mapping the unrecognised case to `unknown_error` rather than throwing
 * keeps an older game able to render a newer failure.
 */
export function toRizError(value: unknown, fallbackCode = SDK_ERRORS.unknownError): RizError {
  if (isRizError(value)) return value;
  if (value && typeof value === 'object') {
    const raw = value as { code?: unknown; message?: unknown; error?: unknown; details?: unknown };
    const code = typeof raw.code === 'string' ? raw.code : fallbackCode;
    const message = typeof raw.message === 'string'
      ? raw.message
      : typeof raw.error === 'string' ? raw.error : code;
    return rizError(code, message, raw.details ?? null);
  }
  if (value instanceof Error) return rizError(fallbackCode, value.message);
  return rizError(fallbackCode, typeof value === 'string' && value ? value : 'Riz returned an unrecognised error.');
}
